import { useEffect, useRef } from "react";

/**
 * Refetch helper for staff-side data surfaces. Two hooks are exported:
 *
 *   - `useFocusRefetch(refetch, deps, { enabled })`
 *     Re-invokes `refetch` whenever the tab regains visibility (returning
 *     from another tab), the window regains focus, or the browser comes
 *     back online. No polling.
 *
 *   - `useFocusedPoll(refetch, intervalMs, deps, { enabled })`
 *     Same as `useFocusRefetch`, plus a `setInterval` poll at `intervalMs`
 *     that pauses when the tab is hidden and resumes (with an immediate
 *     catch-up refetch) when the tab becomes visible again.
 *
 * Why this exists: staff workflows commonly span 3-4 tabs (Today's
 * Bookings / Suite Calendar / Active Sessions / Customer Mgmt). Without a
 * focus-driven refetch, switching back to a tab shows stale data until
 * the next manual refresh. These hooks codify the established
 * `TimesheetReview` / `DailyReviewDashboard` `visibilitychange` pattern
 * so it can be applied uniformly across staff surfaces.
 *
 * Note: this codebase does NOT use TanStack Query — refetch posture is
 * implemented as plain hooks layered on top of existing `useEffect` +
 * `axios`/`staffAPI` data fetchers. The intent matches the TanStack
 * `refetchOnWindowFocus` / `refetchOnReconnect` / `refetchInterval`
 * options.
 */

/**
 * Run `refetch` on tab-visible, window-focus, and network-online events.
 * The handler is wired against the latest `refetch` via a ref so callers
 * do not need to memoize it.
 *
 * @param {() => unknown} refetch  Fired on each focus/visible/online event.
 * @param {Array<unknown>} [deps]  Re-wire when these change. Default `[]`.
 * @param {{ enabled?: boolean }} [options]
 *   When `enabled` is `false`, no listeners are wired and no calls are
 *   made. Useful for surfaces guarded by an auth/admin check.
 */
export function useFocusRefetch(refetch, deps = [], options = {}) {
  const { enabled = true } = options;
  const refetchRef = useRef(refetch);

  // Always read the most recent refetch fn — avoids stale closures
  // and removes the need for callers to wrap in `useCallback`.
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof document === "undefined" || typeof window === "undefined") {
      return undefined;
    }

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        try {
          refetchRef.current && refetchRef.current();
        } catch (_) {
          // Swallow: refetchers manage their own error state.
        }
      }
    };
    const onFocus = () => {
      try {
        refetchRef.current && refetchRef.current();
      } catch (_) {
        // Swallow.
      }
    };
    const onOnline = () => {
      try {
        refetchRef.current && refetchRef.current();
      } catch (_) {
        // Swallow.
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);
}

/**
 * Visibility-aware polling. Calls `refetch` every `intervalMs` while the
 * tab is visible; pauses the interval when hidden; fires a catch-up
 * `refetch` and resumes when the tab becomes visible again. Also calls
 * `refetch` on window focus and network online events.
 *
 * Does NOT fire an initial fetch — the caller is expected to keep its
 * own mount-time fetch (this preserves existing component semantics and
 * lets callers manage their own loading state on first paint).
 *
 * @param {() => unknown} refetch  Fired on each tick and focus event.
 * @param {number} intervalMs      Poll cadence. Pass 0 to disable polling
 *                                 (focus/online refetch still wired).
 * @param {Array<unknown>} [deps]  Re-wire when these change. Default `[]`.
 * @param {{ enabled?: boolean }} [options]
 *   When `enabled` is `false`, no listeners or intervals are wired.
 */
export function useFocusedPoll(refetch, intervalMs, deps = [], options = {}) {
  const { enabled = true } = options;
  const refetchRef = useRef(refetch);
  const intervalRef = useRef(null);

  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof document === "undefined" || typeof window === "undefined") {
      return undefined;
    }

    let cancelled = false;

    const callRefetch = () => {
      if (cancelled) return;
      try {
        refetchRef.current && refetchRef.current();
      } catch (_) {
        // Swallow.
      }
    };

    const startPolling = () => {
      if (!intervalMs || intervalMs <= 0) return;
      if (intervalRef.current) return;
      intervalRef.current = setInterval(callRefetch, intervalMs);
    };
    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        callRefetch();
        startPolling();
      } else {
        stopPolling();
      }
    };
    const onFocus = () => callRefetch();
    const onOnline = () => callRefetch();

    // Initial state: only start polling if the tab is currently visible.
    // The mount-time fetch is the caller's responsibility.
    if (document.visibilityState === "visible") {
      startPolling();
    }

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);

    return () => {
      cancelled = true;
      stopPolling();
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs, ...deps]);
}

export default useFocusedPoll;
